import { Subject } from 'rxjs';
import type { ValidationResponseMessage } from '../../@types/common';
import type { RecordResult } from '../../@types/records';
import { recordApi } from '../../redux/services/record';
import { appDispatch } from '../../redux/store-accessors';
import { BackgroundJobHandler } from './background-job.handler';

export const updateRecordSuccessCallbackMap = new Map<string, Subject<number>>();
export const updateRecordData = new Map<string, RecordResult>();

export class UpdateRecordBackgroundJobHandler extends BackgroundJobHandler<RecordResult> {
  constructor(apiProps: RecordResult, details?: Record<string, unknown>) {
    super(apiProps, details);
  }

  public createBackgroundJob() {
    const jobId = String(this.details?.jobId ?? '');
    const recordData = updateRecordData.get(jobId);
    const stepAdvanced = this.details?.stepAdvanced as boolean | undefined;
    if (!recordData) {
      return Promise.reject(new Error(`No record data found for job ID: ${jobId}`));
    }

    const apiPayload = { ...recordData, stepAdvanced };

    return appDispatch(recordApi.endpoints.updateRecordBackgroundJob.initiate(apiPayload)).unwrap();
  }

  public getJobDetails(backgroundJobId: number) {
    const subscription = appDispatch(
      recordApi.endpoints.getMutateRecordBackgroundJobDetails.initiate(backgroundJobId, { forceRefetch: true })
    );
    return subscription.unwrap().finally(() => subscription.unsubscribe());
  }

  public successCallback({ jobDetails }: Record<string, string>) {
    return new Promise<void>((resolve) => {
      const recordMutationSubject = updateRecordSuccessCallbackMap.get(String(this.details?.jobId ?? ''));
      if (recordMutationSubject && !recordMutationSubject.closed) {
        // For the record update process to be completed as expected
        recordMutationSubject.next(Number(jobDetails));
        recordMutationSubject.complete();

        appDispatch(
          recordApi.util.invalidateTags([
            { type: 'Record', id: jobDetails },
            { type: 'Record', id: 'LIST' },
            'Record',
            'LinkedRecord',
            'ReverseLinkedRecord',
            'SummaryLinkedRecord',
            'AssignedRecords',
            'AssignedRecordsCount',
            'RecordPermissions',
            'AllFormsRecord',
          ])
        );
      }

      updateRecordSuccessCallbackMap.delete(String(this.details?.jobId ?? ''));
      updateRecordData.delete(String(this.details?.jobId ?? ''));
      resolve();
    });
  }

  errorCallback(responseErrors: ValidationResponseMessage[]): void {
    const recordMutationSubject = updateRecordSuccessCallbackMap.get(String(this.details?.jobId ?? ''));
    if (recordMutationSubject && !recordMutationSubject.closed) {
      // For the record update process to be completed and show an error
      // Note: the background job API does not correctly handle the merge conflict errors
      recordMutationSubject.error({ data: { validation: { responseErrors } }, status: 400 });
    }
    updateRecordSuccessCallbackMap.delete(String(this.details?.jobId ?? ''));
  }
}
